import React, { useMemo, useState } from 'react';
import { Drawer } from 'antd';
import { useTypedSelector } from '@hooks';
import { useDispatch } from 'react-redux';

import { setToggleOpenTranscript } from '@redux/actions/mediaViewerAction';
import { useOrganization } from '@components/OrganizationBoundary';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';

import HeadTitle from './components/HeadTitle';
import HeadExtra from './components/HeadExtra';
import CreateTranscript from './components/CreateTranscript';
import TranscriptionLoader from './components/TranscriptionLoader';
import TranscriptionContent from './components/TranscriptionContent';

import './TranscriptDrawer.less';

export type TextModeType = 'captions' | 'transcript';

type TranscriptDrawerProps = {
  isOpenTranscript: boolean;
};

function TranscriptDrawer({ isOpenTranscript }: TranscriptDrawerProps) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization(false);
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const metadata = selectedVersion?.metadata;
  const transcriptionStatus =
    selectedVersion?.metadata && 'transcription' in selectedVersion.metadata
      ? selectedVersion.metadata.transcription.status
      : null;

  const canModifyTranscription =
    !!currentOrganization?.entity?.features.audioTranscription &&
    !!asset?.asset?.permissions.transcribe;

  const [searchValue, setSearchValue] = useState('');
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [textMode, setTextMode] = useState<TextModeType>('transcript');

  const availableTranscripts = useMemo(() => {
    if (transcriptionStatus !== 'ready') return null;
    if (metadata?.type !== 'video' && metadata?.type !== 'audio') return null;
    const _items: Record<string, string> = {};
    if (metadata?.transcription?.url) {
      _items.url = metadata.transcription.url;
    }
    if (metadata?.transcription?.subRipUrl) {
      _items.subRipUrl = metadata.transcription.subRipUrl;
    }
    if (metadata?.transcription?.webVttUrl) {
      _items.subRipUrl = metadata.transcription.webVttUrl;
    }
    return Object.values(_items).length ? _items : null;
  }, [metadata, transcriptionStatus]);

  return (
    <Drawer
      title={
        <HeadTitle
          canModifyTranscription={canModifyTranscription}
          isAutoScrolling={isAutoScrolling}
          setIsAutoScrolling={setIsAutoScrolling}
          availableTranscripts={availableTranscripts}
        />
      }
      zIndex={100}
      extra={
        !!availableTranscripts && (
          <HeadExtra
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            textMode={textMode}
            setTextMode={setTextMode}
          />
        )
      }
      className="media_viewer_transcript-drawer"
      closeIcon={<CrossSvg />}
      placement="right"
      onClose={() => dispatch(setToggleOpenTranscript(false))}
      width={432}
      open={isOpenTranscript}
      afterOpenChange={(open) =>
        !open && dispatch(setToggleOpenTranscript(null))
      }
    >
      {/* TODO: Add error state */}
      {(transcriptionStatus === 'skipped' ||
        transcriptionStatus === 'error') && <CreateTranscript />}
      {transcriptionStatus === 'pending' && <TranscriptionLoader />}
      {transcriptionStatus === 'ready' && availableTranscripts && (
        <TranscriptionContent
          availableTranscripts={availableTranscripts}
          textMode={textMode}
          isAutoScrolling={isAutoScrolling}
          searchValue={searchValue}
        />
      )}
    </Drawer>
  );
}

export default TranscriptDrawer;
